import * as React from "react";
import { dummyPlayerCard, PlayerCardTheme } from "../../../../services/bedrock";
import { Button, Form, Input, Label, Popup } from "semantic-ui-react";
import _ from "lodash";
import { HexColorPicker } from "react-colorful";
import { useContext, useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { PlayerCardSettingsContext } from "../../Contexts";
import DetailsPage from "../../DetailsPage";

interface ThemeModalProps {
  active: boolean;
  theme: PlayerCardTheme;
  onThemeSaved: (theme: PlayerCardTheme) => void;
  onClose: () => void;
}

const ThemeModal: React.FC<ThemeModalProps> = ({ active, theme, onThemeSaved, onClose }) => {
  const methods = useForm<PlayerCardTheme>({ defaultValues: theme });
  const {
    reset,
    control,
    handleSubmit,
    formState: { isDirty, isSubmitting },
    getValues,
  } = methods;

  const { Themes, Preview, PlayerCards, User } = useContext(PlayerCardSettingsContext);

  const colorProperties: (keyof PlayerCardTheme)[] = [
    "pageBackground",
    "pageForeground",
    "widgetBackground",
    "widgetForeground",
  ];
  useEffect(() => reset(theme), [theme]);

  const watchedProperties = useWatch({ control, name: colorProperties });

  useEffect(() => {
    if (!active) return;
    const playerCard =
      User.isPlayer && PlayerCards.selectedPlayerCard ? PlayerCards.selectedPlayerCard : dummyPlayerCard;
    Preview.setPlayerCard({ ...playerCard, themeId: theme.id, theme: getValues() });
  }, [watchedProperties, active]);

  const saveTheme = handleSubmit(data => {
    if (!isDirty) return;

    Themes.saveTheme(data).then(onThemeSaved);
  });

  const cancel = (close: boolean) => {
    if (!isDirty || window.confirm("Are you sure you want to discard your changes?")) {
      reset(theme);
      if (close) onClose();
    }
  };

  const colorRules = {
    required: "This is required",
    validate: (value: any): string | undefined => {
      return value.match(/^#?([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/) === null ? "Must be a valid hex color code" : undefined;
    },
  };

  const content = (
    <div className={`playerCardThemeModal ${active ? "active" : ""}`}>
      <Form>
        <Controller
          name="name"
          control={control}
          rules={{ required: "This is required" }}
          render={({ field, fieldState: { error } }) => {
            return (
              <Form.Field className="playerCardField playerCardTemplateName" error={!!error}>
                <div className="playerCardFieldLabel">Name</div>
                <Input {...field} placeholder="Theme Name" />
                {error && (
                  <Label basic prompt pointing>
                    {error.message}
                  </Label>
                )}
              </Form.Field>
            );
          }}
        />

        <div className="colorFields">
          {colorProperties.map(colorProperty => (
            <Controller
              name={colorProperty}
              control={control}
              rules={colorRules}
              render={({ field, fieldState: { error } }) => (
                <Form.Field>
                  <label>{_.startCase(colorProperty)}</label>
                  <Popup
                    position="bottom center"
                    content={
                      <div className="colorPickerPopupContent">
                        <HexColorPicker color={field.value as string} onChange={field.onChange} />
                        <Form.Input {...field} className="colorPickerInput" />
                      </div>
                    }
                    trigger={<div className="colorPreview" style={{ backgroundColor: field.value as string }} />}
                    flowing
                    on="click"
                  />
                  {error && (
                    <Label basic prompt pointing>
                      {error.message}
                    </Label>
                  )}
                </Form.Field>
              )}
            />
          ))}
        </div>
      </Form>
      <div className="actions">
        <Button content="Cancel" disabled={!isDirty} loading={isSubmitting} secondary onClick={() => cancel(false)} />
        <Button content="Save" disabled={!isDirty} loading={isSubmitting} primary onClick={saveTheme} />
      </div>
    </div>
  );
  if (active) {
    return <DetailsPage onClose={() => cancel(true)}>{content}</DetailsPage>;
  } else {
    return content;
  }
};
export default ThemeModal;
